import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, AlertCircle, CheckCircle, Copy } from 'lucide-react';
import api from '../../services/api';
import type { Patient } from '../../../../shared/professional-types';

interface CreatePatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientCreated: (patient: Patient) => void;
}

interface Credentials {
  username: string;
  password: string;
}

export default function CreatePatientModal({
  isOpen,
  onClose,
  onPatientCreated,
}: CreatePatientModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [copied, setCopied] = useState<'username' | 'password' | null>(null);

  const [formData, setFormData] = useState({
    identificador: '',
    diagnostico: '',
    notas_iniciales: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.identificador) {
      setError('El identificador es obligatorio');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/professional/patients', formData);

      if (response.data.ok) {
        setCredentials(response.data.data.credentials);
        onPatientCreated(response.data.data.patient);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, type: 'username' | 'password') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleClose = () => {
    setFormData({
      identificador: '',
      diagnostico: '',
      notas_iniciales: '',
    });
    setCredentials(null);
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={!credentials ? handleClose : undefined}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="glass-container max-w-2xl w-full pointer-events-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <UserPlus className="w-6 h-6 text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-display font-bold">
                    {credentials ? '✓ Paciente Creado' : 'Crear Nuevo Paciente'}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Credenciales (después de crear) */}
              {credentials ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-green-300 mb-1">
                          Paciente creado exitosamente
                        </h3>
                        <p className="text-sm text-green-200/80">
                          Guarda estas credenciales temporales. El paciente debe cambiar la contraseña en su primer acceso.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Usuario */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Usuario
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={credentials.username}
                        readOnly
                        className="input-neureka flex-1 bg-white/5 cursor-text"
                      />
                      <button
                        onClick={() => handleCopy(credentials.username, 'username')}
                        className="btn-neureka-secondary flex-shrink-0"
                      >
                        {copied === 'username' ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Contraseña */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Contraseña Temporal
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={credentials.password}
                        readOnly
                        className="input-neureka flex-1 bg-white/5 cursor-text font-mono"
                      />
                      <button
                        onClick={() => handleCopy(credentials.password, 'password')}
                        className="btn-neureka-secondary flex-shrink-0"
                      >
                        {copied === 'password' ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button onClick={handleClose} className="btn-neureka">
                      Cerrar
                    </button>
                  </div>
                </div>
              ) : (
                /* Formulario */
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Error */}
                  {error && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-300 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Identificador del centro */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      ID de paciente <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.identificador}
                      onChange={(e) =>
                        setFormData({ ...formData, identificador: e.target.value })
                      }
                      className="input-neureka"
                      placeholder="Código o número asignado por el centro"
                    />
                    <p className="mt-1 text-xs text-white/40">
                      Usa el identificador interno de tu centro. No se almacenará ningún nombre personal.
                    </p>
                  </div>

                  {/* Diagnóstico */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Diagnóstico
                    </label>
                    <input
                      type="text"
                      value={formData.diagnostico}
                      onChange={(e) =>
                        setFormData({ ...formData, diagnostico: e.target.value })
                      }
                      className="input-neureka"
                      placeholder="Ej: Deterioro cognitivo leve"
                    />
                  </div>

                  {/* Notas Iniciales */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Notas Iniciales
                    </label>
                    <textarea
                      value={formData.notas_iniciales}
                      onChange={(e) =>
                        setFormData({ ...formData, notas_iniciales: e.target.value })
                      }
                      className="input-neureka resize-none"
                      rows={4}
                      placeholder="Observaciones iniciales del paciente..."
                    />
                  </div>

                  {/* Botones */}
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-neureka-secondary"
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-neureka"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creando...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          Crear Paciente
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
